import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Clock, BarChart3, Star, Users, PlayCircle, FileText,
  ChevronRight, Check, Briefcase, Heart, Loader2
} from 'lucide-react';
import { Button }   from '@/components/ui/button';
import { Badge }    from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { getUser }  from '@/lib/data';

const API_BASE = 'https://xceededge.com'; // ← update this

export default function CourseDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const user         = getUser();

  const [course,      setCourse]      = useState(null);
  const [enrolled,    setEnrolled]    = useState(false);
  const [wishlisted,  setWishlisted]  = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [enrolling,   setEnrolling]   = useState(false);
  const [wishlisting, setWishlisting] = useState(false);
  const [error,       setError]       = useState('');

  // ── 1. Fetch course data + user status ──────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setError('');

    // Fetch course details
    const courseReq = axios.get(`${API_BASE}/course-detail.php?id=${id}`);

    // Fetch enrollment/wishlist status (only if logged in)
    const statusReq = user
      ? (() => {
          const fd = new FormData();
          fd.append('action',    'status');
          fd.append('course_id', id);
          fd.append('user_id',   user.id);
          return axios.post(`${API_BASE}/course-detail.php`, fd);
        })()
      : Promise.resolve({ data: { enrolled: false, wishlisted: false } });

    Promise.all([courseReq, statusReq])
      .then(([courseRes, statusRes]) => {
        if (courseRes.data.success) {
          setCourse(courseRes.data.data);
        } else {
          setError('Course not found.');
        }
        setEnrolled(statusRes.data.enrolled);
        setWishlisted(statusRes.data.wishlisted);
      })
      .catch(() => setError('Failed to load course. Please try again.'))
      .finally(() => setLoading(false));
  }, [id]);

  // ── 2. Enroll ───────────────────────────────────────────────────────────────
  const handleEnroll = () => {
    if (!user) { navigate('/auth'); return; }

    setEnrolling(true);
    const fd = new FormData();
    fd.append('action',    'enroll');
    fd.append('course_id', id);
    fd.append('user_id',   user.id);

    axios.post(`${API_BASE}/course-detail.php`, fd)
      .then(res => {
        if (res.data.success) setEnrolled(true);
      })
      .catch(() => alert('Enrollment failed. Please try again.'))
      .finally(() => setEnrolling(false));
  };

  // ── 3. Wishlist toggle ──────────────────────────────────────────────────────
  const handleWishlist = () => {
    if (!user) { navigate('/auth'); return; }

    setWishlisting(true);
    const fd = new FormData();
    fd.append('action',    'wishlist');
    fd.append('course_id', id);
    fd.append('user_id',   user.id);

    axios.post(`${API_BASE}/course-detail.php`, fd)
      .then(res => {
        if (res.data.success) setWishlisted(res.data.wishlisted);
      })
      .catch(() => alert('Wishlist update failed.'))
      .finally(() => setWishlisting(false));
  };

  // ── Loading state ───────────────────────────────────────────────────────────
  if (loading) return (
    <div className="pt-32 flex justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  // ── Error / not found ───────────────────────────────────────────────────────
  if (error || !course) return (
    <div className="pt-32 text-center">
      <h1 className="font-heading text-2xl font-bold">{error || 'Course not found'}</h1>
      <Link to="/courses" className="text-primary mt-4 inline-block">← Back to courses</Link>
    </div>
  );

  const totalLessons = course.curriculum.reduce((sum, m) => sum + Number(m.lessons), 0);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/courses" className="hover:text-primary">Courses</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground truncate">{course.title}</span>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">

          {/* ── Main Content ── */}
          <div className="lg:col-span-2 space-y-8">

            {/* Header */}
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                {course.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                ))}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold">{course.title}</h1>
              <p className="text-muted-foreground mt-3 text-lg leading-relaxed">{course.description}</p>

              <div className="flex flex-wrap items-center gap-6 mt-6">
                <div className="flex items-center gap-1.5">
                  <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                  <span className="font-semibold">{course.rating}</span>
                  <span className="text-muted-foreground text-sm">({course.review_count} reviews)</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" /> {Number(course.students).toLocaleString()} students
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" /> {course.duration}
                </div>
                {/* <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <BarChart3 className="w-4 h-4" /> {course.level}
                </div> */}
              </div>
            </div>

            {/* Instructor */}
            <div className="bg-card rounded-xl border p-5 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="font-heading font-bold text-primary text-lg">
                  {course.instructor_name[0]}
                </span>
              </div>
              <div>
                <p className="font-semibold">{course.instructor_name}</p>
                <p className="text-sm text-muted-foreground">{course.instructor_title}</p>
              </div>
            </div>

            {/* Curriculum */}
            <div>
              <h2 className="font-heading text-2xl font-bold mb-4">Curriculum</h2>
              <p className="text-sm text-muted-foreground mb-4">
                {course.curriculum.length} modules · {totalLessons} lessons
              </p>
              <Accordion type="single" collapsible className="space-y-2">
                {course.curriculum.map((module, i) => (
                  <AccordionItem key={i} value={`module-${i}`} className="bg-card rounded-lg border px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 text-left">
                        <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                          {i + 1}
                        </span>
                        <div>
                          <p className="font-medium text-sm">{module.module}</p>
                          <p className="text-xs text-muted-foreground">
                            {module.lessons.split(",").map(item => item.trim()).length} lessons · {module.duration}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-11 space-y-2 pb-2">
                        {module.lessons.split(",").map(item => item.trim()).map((lesson, j) => (
                          <div key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <PlayCircle className="w-4 h-4 text-primary/50" />
                            {lesson}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Projects */}
            <div>
              <h2 className="font-heading text-2xl font-bold mb-4">Projects You'll Build</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {course.projects.map((project, i) => (
                  <div key={i} className="bg-card rounded-lg border p-4">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center mb-3">
                      <FileText className="w-5 h-5 text-accent" />
                    </div>
                    <p className="font-medium text-sm">{project}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Career Outcomes */}
            <div>
              <h2 className="font-heading text-2xl font-bold mb-4">Career Outcomes</h2>
              <div className="flex flex-wrap gap-3">
                {course.career_outcomes.map((outcome, i) => (
                  <div key={i} className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                    <Briefcase className="w-4 h-4" />
                    <span className="text-sm font-medium">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Reviews */}
            <div>
              <h2 className="font-heading text-2xl font-bold mb-4">Student Reviews</h2>
              {course.reviews.length === 0 ? (
                <p className="text-muted-foreground text-sm">No reviews yet.</p>
              ) : (
                <div className="space-y-4">
                  {course.reviews.map((review, i) => (
                    <div key={i} className="bg-card rounded-lg border p-5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                            {review.name[0]}
                          </div>
                          <span className="font-medium text-sm">{review.name}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{review.date}</span>
                      </div>
                      <div className="flex gap-0.5 mb-2">
                        {Array.from({ length: Number(review.rating) }).map((_, j) => (
                          <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground">{review.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Pricing Card ── */}
          <div>
            <div className="sticky top-24 bg-card rounded-xl border shadow-lg overflow-hidden">
              <div className="aspect-video bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <PlayCircle className="w-16 h-16 text-primary/40" />
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-baseline gap-3">
                  <Badge className="bg-accent text-accent-foreground">
                    {Math.round((1 - course.price / course.original_price) * 100)}% OFF
                  </Badge>
                </div>

                {enrolled ? (
                  <Link to={`/player/${course.id}`} className="block">
                    <Button className="w-full bg-green-600 hover:bg-green-700 h-12 text-base gap-2">
                      <PlayCircle className="w-5 h-5" /> Continue Learning
                    </Button>
                  </Link>
                ) : (
                  <Button
                    onClick={handleEnroll}
                    disabled={enrolling}
                    className="w-full bg-primary hover:bg-primary/90 h-12 text-base gap-2"
                  >
                    {enrolling && <Loader2 className="w-4 h-4 animate-spin" />}
                    {enrolling ? 'Enrolling...' : 'Enroll Now'}
                  </Button>
                )}

                <Button
                  variant="outline"
                  className="w-full gap-2"
                  disabled={wishlisting}
                  onClick={handleWishlist}
                >
                  {wishlisting
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Heart className={`w-4 h-4 ${wishlisted ? 'fill-red-500 text-red-500' : ''}`} />
                  }
                  {wishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
                </Button>

                <div className="space-y-3 pt-4 border-t">
                  {[
                    `${course.duration} of content`,
                    `${totalLessons} lessons`,
                    `${course.projects.length} real-world projects`,
                    'Certificate of completion',
                    'Lifetime access',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </motion.div>
  );
}